// src/components/RegistrationScreen.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import cloudinaryService from '../services/cloudinaryService';
import { Provider, Client } from '../models';
import { FiCamera, FiUpload, FiUser, FiPhone, FiMapPin, FiBriefcase, FiAlertCircle } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';

// ── مكوّن صغير قابل لإعادة الاستخدام لرفع الوثائق ──
const UploadField = ({ label, value, onUpload, onClear, uploading, loading }) => (
  <div>
    <p className="text-sm font-medium text-gray-700 mb-2">{label}</p>
    {value ? (
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
        <div className="flex items-center">
          <FiUpload className="text-green-600 ml-2" />
          <span className="text-green-800 font-medium text-sm">تم الرفع بنجاح ✅</span>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={loading}
          className="text-red-600 hover:text-red-800 disabled:opacity-50 text-sm"
        >
          حذف
        </button>
      </div>
    ) : (
      <button
        type="button"
        onClick={onUpload}
        disabled={uploading || loading}
        className="w-full bg-gray-900 text-white py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
      >
        {uploading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
            جاري الرفع...
          </>
        ) : (
          <>
            <FiUpload className="ml-2" />
            رفع الصورة
          </>
        )}
      </button>
    )}
  </div>
);

const RegistrationScreen = () => {
  const { user, userType, refreshUserType } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [states, setStates] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');

  // ✅ وثائق الهوية بدل documentUrl الواحدة
  const [idFrontUrl, setIdFrontUrl] = useState('');
  const [idBackUrl, setIdBackUrl] = useState('');
  const [professionUrl, setProfessionUrl] = useState('');

  const [loadError, setLoadError] = useState(null);

  // 🆕 ملء البيانات من Google إذا كانت متوفرة
  const [formData, setFormData] = useState({
    fullName: user?.user_metadata?.full_name || user?.user_metadata?.name || '',
    phone: '',
    address: '',
    stateId: null,
    professionId: null,
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // إذا كان المستخدم مسجل بالفعل، انتقل مباشرة
    if (userType) {
      console.log('👤 User already registered, redirecting to:', userType === 'provider' ? '/provider' : '/client');
      navigate(userType === 'provider' ? '/provider' : '/client');
    }
  }, [userType, navigate]);

  const loadData = async () => {
    setDataLoading(true);
    setLoadError(null);

    try {
      console.log('📥 Loading states and professions...');

      // إضافة تأخير قصير للسماح للجلسة بالاستقرار
      await new Promise(resolve => setTimeout(resolve, 500));

      const [statesData, professionsData] = await Promise.all([
        supabaseService.getStates(),
        supabaseService.getProfessions()
      ]);

      console.log('✅ Data loaded:', {
        states: statesData?.length || 0,
        professions: professionsData?.length || 0
      });

      if (!statesData || statesData.length === 0) {
        throw new Error('فشل تحميل الولايات');
      }

      if (!professionsData || professionsData.length === 0) {
        throw new Error('فشل تحميل المهن');
      }

      setStates(statesData);
      setProfessions(professionsData);
    } catch (error) {
      console.error("❌ Error loading data:", error);
      setLoadError(error.message);
      toast.error('فشل تحميل البيانات. جاري إعادة المحاولة...');

      // إعادة المحاولة مرة واحدة
      setTimeout(() => {
        loadData();
      }, 2000);
    } finally {
      setDataLoading(false);
    }
  };

  // ✅ target: 'avatar' | 'idFront' | 'idBack' | 'profession'
  const pickImage = async (target = 'avatar') => {
    if (uploading) return;

    try {
      setUploading(true);

      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';

      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          try {
            const url = await cloudinaryService.uploadImage(
              file,
              target === 'avatar' ? 'avatar' : 'document'
            );

            if (url) {
              switch (target) {
                case 'avatar':
                  setAvatarUrl(url);
                  toast.success('✅ تم رفع الصورة بنجاح');
                  break;
                case 'idFront':
                  setIdFrontUrl(url);
                  toast.success('✅ تم رفع صورة البطاقة (الوجه الأمامي)');
                  break;
                case 'idBack':
                  setIdBackUrl(url);
                  toast.success('✅ تم رفع صورة البطاقة (الوجه الخلفي)');
                  break;
                case 'profession':
                  setProfessionUrl(url);
                  toast.success('✅ تم رفع وثيقة إثبات المهنة');
                  break;
                default:
                  break;
              }
            }
          } catch (uploadError) {
            console.error('Upload error:', uploadError);
            toast.error('فشل رفع الصورة. حاول مرة أخرى.');
          }
        }
        setUploading(false);
      };

      input.click();
    } catch (error) {
      console.error("❌ Error picking image:", error);
      toast.error('فشل اختيار الصورة');
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName || !formData.phone || !formData.address || !formData.stateId) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    if (activeTab === 0 && !formData.professionId) {
      toast.error('يرجى اختيار المهنة');
      return;
    }

    // ✅ التحقق من رفع صورتي البطاقة الوطنية (إجباري لمزود الخدمة)
    if (activeTab === 0 && (!idFrontUrl || !idBackUrl)) {
      toast.error('يرجى رفع صورتي البطاقة الوطنية (الوجه الأمامي والخلفي)');
      return;
    }

    setLoading(true);

    try {
      const isProvider = activeTab === 0;

      console.log('🚀 Submitting registration for:', isProvider ? 'Provider' : 'Client');

      if (isProvider) {
        const provider = new Provider({
          id: user.id,
          email: user.email,
          fullName: formData.fullName,
          avatarUrl,
          stateId: formData.stateId,
          address: formData.address,
          professionId: formData.professionId,
          phone: formData.phone,
        });

        await supabaseService.createProvider(provider);
        console.log('✅ Provider created successfully');

        // ✅ إرسال idFrontUrl / idBackUrl / professionUrl معاً
        if (idFrontUrl || idBackUrl || professionUrl) {
          await supabaseService.createProviderDocument(user.id, {
            idFrontUrl,
            idBackUrl,
            professionUrl,
          });
          console.log('✅ Provider document uploaded');
        }
      } else {
        await supabaseService.createClient({
          id: user.id,
          email: user.email,
          fullName: formData.fullName,
          avatarUrl: avatarUrl,
          stateId: formData.stateId,
          address: formData.address,
          phone: formData.phone,
        });
        console.log('✅ Client created successfully');
      }

      toast.success('✅ تم إكمال بياناتك بنجاح! جاري الانتقال...');

      // تحديث نوع المستخدم
      const updatedType = await refreshUserType();
      console.log('👤 Updated user type:', updatedType);

      // 🆕 التحقق من وجود طلب معلق
      const pendingRequest = sessionStorage.getItem('pending_request');

      // الانتظار قليلاً ثم الانتقال
      setTimeout(() => {
        // 🆕 التحقق من وجود صفحة مزود معلقة للعودة إليها
        const pendingProviderPage = sessionStorage.getItem('pending_provider_page');
        if (pendingProviderPage) {
          sessionStorage.removeItem('pending_provider_page');
          console.log('🚀 Navigating to pending provider page:', pendingProviderPage);
          navigate(pendingProviderPage, { replace: true });
          return;
        }

        const destination = isProvider ? '/provider' : '/client';
        console.log('🚀 Navigating to:', destination);

        // 🆕 تمرير معلومة الطلب المعلق
        navigate(destination, {
          state: {
            hasPendingRequest: !!pendingRequest
          }
        });
      }, 1500);

    } catch (error) {
      console.error('❌ Error during registration:', error);
      toast.error(`خطأ: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: 'beforeChildren',
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  // إذا لم يكن هناك مستخدم، أعد التوجيه
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <p className="text-gray-600 mb-4">جاري التحقق من بيانات الجلسة...</p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
        </div>
      </div>
    );
  }

  // إذا كانت البيانات قيد التحميل
  if (dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-2">جاري تحميل البيانات...</p>
          <p className="text-gray-400 text-sm">يرجى الانتظار</p>
        </div>
      </div>
    );
  }

  // إذا كان هناك خطأ في التحميل
  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="text-center max-w-md">
          <FiAlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">فشل تحميل البيانات</h2>
          <p className="text-gray-600 mb-6">{loadError}</p>
          <button
            onClick={loadData}
            className="bg-gray-900 text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-gray-900 text-white p-4">
        <div className="max-w-md mx-auto">
          <h1 className="text-2xl font-bold text-center">إكمال بياناتك</h1>
          {user?.email && (
            <p className="text-gray-300 text-center text-sm mt-2">
              مرحباً بك في Khidma! 👋
            </p>
          )}
        </div>
      </div>

      <div className="max-w-md mx-auto p-4">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
            <div className="flex">
              <button
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  activeTab === 0
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab(0)}
              >
                مزود خدمة
              </button>
              <button
                className={`flex-1 py-3 text-center font-medium transition-colors ${
                  activeTab === 1
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab(1)}
              >
                زبون
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Upload Section */}
            <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">الصورة الشخصية</h3>
              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <FiUser className="text-4xl text-gray-400" />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => pickImage('avatar')}
                    disabled={uploading || loading}
                    className="absolute bottom-0 left-0 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {uploading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                    ) : (
                      <FiCamera className="text-gray-900" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500 text-center">اضغط على الكاميرا لرفع صورة شخصية</p>
              </div>
            </motion.div>

            {/* Form Fields Section */}
            <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4">
                {activeTab === 0 ? 'معلومات مزود الخدمة' : 'معلومات العميل'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-700 mb-2 font-medium">الاسم الكامل</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <FiUser className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="w-full py-3 pr-10 pl-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 mb-2 font-medium">رقم الهاتف</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <FiPhone className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      className="w-full py-3 pr-10 pl-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 mb-2 font-medium">الولاية</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-10">
                      <FiMapPin className="text-gray-400" />
                    </div>
                    <select
                      className="w-full py-3 pr-10 pl-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none bg-white"
                      value={formData.stateId || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({ ...formData, stateId: value ? parseInt(value) : null });
                        console.log('Selected state:', value);
                      }}
                      required
                      disabled={loading || states.length === 0}
                    >
                      <option value="">اختر الولاية</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {states.length === 0 && (
                    <p className="text-red-500 text-xs mt-1">لم يتم تحميل الولايات</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 mb-2 font-medium">العنوان</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <FiMapPin className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="w-full py-3 pr-10 pl-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {activeTab === 0 && (
                  <div>
                    <label className="block text-gray-700 mb-2 font-medium">المهنة</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-10">
                        <FiBriefcase className="text-gray-400" />
                      </div>
                      <select
                        className="w-full py-3 pr-10 pl-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 appearance-none bg-white"
                        value={formData.professionId || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormData({ ...formData, professionId: value ? parseInt(value) : null });
                          console.log('Selected profession:', value);
                        }}
                        required
                        disabled={loading || professions.length === 0}
                      >
                        <option value="">اختر المهنة</option>
                        {professions.map((profession) => (
                          <option key={profession.id} value={profession.id}>
                            {profession.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {professions.length === 0 && (
                      <p className="text-red-500 text-xs mt-1">لم يتم تحميل المهن</p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>

            {/* Document Upload Section for Providers */}
            {activeTab === 0 && (
              <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md p-6 space-y-5">
                <h3 className="text-lg font-bold">وثائق التحقق من الهوية</h3>
                <p className="text-xs text-gray-500 -mt-3">
                  يرجى رفع صورة واضحة للبطاقة الوطنية (الوجهين)، ويمكنك أيضاً إرفاق ما يثبت نشاطك المهني
                </p>

                <UploadField
                  label="صورة البطاقة الوطنية (الوجه الأمامي) *"
                  value={idFrontUrl}
                  onUpload={() => pickImage('idFront')}
                  onClear={() => setIdFrontUrl('')}
                  uploading={uploading}
                  loading={loading}
                />

                <UploadField
                  label="صورة البطاقة الوطنية (الوجه الخلفي) *"
                  value={idBackUrl}
                  onUpload={() => pickImage('idBack')}
                  onClear={() => setIdBackUrl('')}
                  uploading={uploading}
                  loading={loading}
                />

                <UploadField
                  label="وثيقة إثبات المهنة أو ما يثبت نشاطك (اختياري)"
                  value={professionUrl}
                  onUpload={() => pickImage('profession')}
                  onClear={() => setProfessionUrl('')}
                  uploading={uploading}
                  loading={loading}
                />
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.div variants={itemVariants}>
              <button
                type="submit"
                disabled={loading || uploading || states.length === 0 || (activeTab === 0 && professions.length === 0)}
                className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>جاري الحفظ...</span>
                  </div>
                ) : (
                  'إكمال التسجيل'
                )}
              </button>
            </motion.div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RegistrationScreen;